import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import get from 'lodash.get';
import { loadScheduleAction } from 'redux/modules/tve';

/**
 * Higher-order component that provides schedule data and management
 * @param {React.Component} WrappedComponent - Component to wrap with schedule provider functionality
 * @returns {React.Component} Connected component with schedule data and loading functionality
 */
export default function ScheduleProviderExport(WrappedComponent) {
  /**
   * ScheduleProvider component manages TV schedule loading and updates
   * @param {object} props - Component props
   * @param {object} props.epgCurrent - Current EPG schedule item
   * @param {boolean} props.epgLoading - Loading state for EPG data
   * @param {Array} props.epgUpcoming - Array of upcoming schedule items
   * @param {Function} props.loadSchedule - Function to load schedule data
   * @param {string} props.vertical - Vertical identifier for schedule loading
   * @returns {React.Element} Wrapped component with schedule props
   */
  const ScheduleProvider = ({
    epgCurrent = {},
    epgLoading = false,
    epgUpcoming = [],
    loadSchedule = Function.prototype,
    vertical,
    ...props
  }) => {
    const scheduleTimeoutRef = useRef(null);
    const previousGuidRef = useRef(null);

    // Initial load on mount only
    useEffect(() => {
      loadSchedule(vertical);
    }, [vertical]);

    // Handle schedule updates when program changes
    useEffect(() => {
      const currentGuid = epgCurrent.guid;

      // Check if the program actually changed
      if (currentGuid && currentGuid !== previousGuidRef.current) {
        previousGuidRef.current = currentGuid;

        // Clear any existing timeout
        clearTimeout(scheduleTimeoutRef.current);

        // Calculate time until end of current show
        const { startTime } = epgCurrent;
        const runtime = get(epgCurrent, 'program.runtime', 3600);
        const diff = (startTime + (runtime * 1000)) - (new Date()).getTime();

        // Set timeout to update schedule at end of current show
        if (!Number.isNaN(diff) && diff > 0) {
          scheduleTimeoutRef.current = setTimeout(() => {
            loadSchedule(vertical);
          }, diff);
        }
      }

      return () => {
        clearTimeout(scheduleTimeoutRef.current);
      };
    }, [epgCurrent.guid, epgCurrent.startTime, loadSchedule, vertical]);

    return (
      <WrappedComponent
        epgCurrent={epgCurrent}
        epgLoading={epgLoading}
        epgUpcoming={epgUpcoming}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      />
    );
  };

  // Connect higher order component
  const mapStateToProps = ({ shared, tve }) => ({
    epgCurrent: tve.epgCurrent,
    epgLoading: tve.epgLoading,
    epgUpcoming: tve.epgUpcoming,
    vertical: shared.vertical,
  });

  const mapActionsToProps = (dispatch) => ({
    loadSchedule: (vertical) => dispatch(loadScheduleAction(vertical)),
  });

  return connect(mapStateToProps, mapActionsToProps)(
    ScheduleProvider,
  );
}
